import { Accordion, AccordionItemTypes } from "@components/Accordion";
import { useEmbedMode } from "@components/donate/DonateV3/EmbedModeContext";
import Big from "big.js";
import React from "react";

import { FundraiserResponse } from "@every.org/common/src/codecs/entities";
import {
  EVERYDOTORG_NONPROFIT_IDS,
  GIVING_MULTIPLIER_NONPROFIT_ID,
  MYRIAD_NONPROFIT_ID,
  SPECIAL_FUNDRAISER_NONPROFIT_ID,
  UNDP_NONPROFIT_ID,
} from "@every.org/common/src/entity/constants";
import {
  Currency,
  DisbursementTypeUserFacing,
  DonationFlowPaymentOption,
  NonprofitType,
} from "@every.org/common/src/entity/types";
import {
  getRoutePath,
  URLFormat,
  ClientRouteName,
} from "@every.org/common/src/helpers/clientRoutes";
import { displayCurrencyValue } from "@every.org/common/src/helpers/currency";

import { Link } from "src/components/Link";
import {
  DAF_DEDUCTIBLE_STATEMENT,
  IRA_DEDUCTIBLE_STATEMENT,
  getTaxDeductibleStatement,
} from "src/components/donate/DonateV3/Footer";
import { useParentNonprofit } from "src/context/NonprofitsContext/hooks";
import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { DonateV3GridCard } from "src/pages/DonateV3/styles";
import { LinkAppearance } from "src/styles/link";
import { mailToLink } from "src/utility/helpers";
import { hasParentId } from "src/utility/nonprofit";

const IRA_QCD_SUPPORT_URL =
  "https://support.every.org/hc/en-us/articles/55340641316115-How-can-I-donate-from-my-IRA-to-a-charity-using-Every-org";
const MYRIAD_EMAIL = "donations@myriadusa.org";
const MYRIAD_PHONE = "(212) 713 7660";
const UNDP_EMAIL = "updates.reply@undp.org";

function getDisbursementType(
  nonprofit: ContextNonprofit,
  parentNonprofit?: ContextNonprofit
) {
  if (
    nonprofit.type === NonprofitType.PROJECT &&
    parentNonprofit?.disbursementType
  ) {
    return parentNonprofit.disbursementType;
  }
  return nonprofit.disbursementType;
}

export function getGranteeName(
  nonprofit: ContextNonprofit,
  parentNonprofit?: ContextNonprofit
) {
  if (nonprofit.metadata?.granteeName) {
    return nonprofit.metadata?.granteeName;
  }
  const name = nonprofit.metadata?.prefixWithThe
    ? `the ${nonprofit.name}`
    : nonprofit.name;
  if (hasParentId(nonprofit, MYRIAD_NONPROFIT_ID)) {
    return `the American Friends Fund set up for ${name} hosted at Myriad USA`;
  }
  if (parentNonprofit) {
    return `the fund for ${name} hosted at ${parentNonprofit.name}`;
  }
  if (nonprofit.id === SPECIAL_FUNDRAISER_NONPROFIT_ID) {
    return "the nonprofits";
  }
  return name;
}

function getDisbursementDescription(
  nonprofit: ContextNonprofit,
  parentNonprofit: ContextNonprofit | undefined,
  // The embedded (iframed) flow must not link out of the donate flow
  { linkToDisbursements }: { linkToDisbursements: boolean }
) {
  const granteeName = getGranteeName(nonprofit, parentNonprofit);
  const disbursementType = getDisbursementType(nonprofit, parentNonprofit);

  if (nonprofit.holdDisbursements) {
    return null;
  }

  if (nonprofit.id === GIVING_MULTIPLIER_NONPROFIT_ID) {
    return null;
  }

  if (disbursementType === DisbursementTypeUserFacing.STRIPE_CONNECT) {
    return <span>On a weekly basis, Every.org grants to {granteeName}.</span>;
  }

  if (disbursementType === DisbursementTypeUserFacing.MANUAL) {
    return (
      <span>
        Every.org grants directly to {granteeName} on a regular basis.
      </span>
    );
  }

  const isPayPal =
    disbursementType === DisbursementTypeUserFacing.PAYPAL_GRANTS;

  return (
    <span>
      We then partner with {isPayPal ? "PayPal Grants" : "For Good"} to grant to{" "}
      {granteeName} on a{" "}
      {linkToDisbursements ? (
        <Link
          appearance={LinkAppearance.HYPERLINK_UNCOLORED}
          to={getRoutePath({
            name: ClientRouteName.DISBURSEMENTS,
            format: URLFormat.RELATIVE,
            fragment: "timing",
          })}
          data-tname={"DonationFaq--mounthlyDonate"}
        >
          monthly basis
        </Link>
      ) : (
        "monthly basis"
      )}
      .
    </span>
  );
}

function getNfgDisclaimer(
  nonprofit: ContextNonprofit,
  parentNonprofit?: ContextNonprofit
) {
  const granteeName = getGranteeName(nonprofit, parentNonprofit);
  const disbursementType = getDisbursementType(nonprofit, parentNonprofit);

  // Ideally we could hide this if all the organizations have connected bank
  // deposit info, but that would require a more complex check.
  if (nonprofit.id === SPECIAL_FUNDRAISER_NONPROFIT_ID) {
    return (
      <p>
        For organizations that have not added bank deposit info to Every.org
        yet, there may be a disbursement fee of 1.5% charged by our disbursement
        partner For Good.
      </p>
    );
  }
  if (
    // If we disburse through some strange method (eg fund account, giving multiplier, FarmKind, etc) set `metadata.directDisbursement` to not show NFG message
    !disbursementType ||
    disbursementType === DisbursementTypeUserFacing.NFG_BATCH_FILE
  ) {
    return (
      <p>
        Because {granteeName} has not added bank deposit info to Every.org yet,
        we currently grant to them through For Good, who charges a 1.5%
        disbursement fee.
      </p>
    );
  }
  return null;
}

function getEveryNoPlatformFee() {
  return (
    <p>
      Every.org does not charge any platform fee of our own, because we are a
      nonprofit. Instead, we rely on the generosity of donors to fund our
      mission of philanthropy for everyone.
    </p>
  );
}

function getFeeDescription(
  paymentOption: DonationFlowPaymentOption | undefined,
  nonprofit: ContextNonprofit,
  parentNonprofit?: ContextNonprofit
) {
  switch (paymentOption) {
    case DonationFlowPaymentOption.PAYMENT_REQUEST:
    case DonationFlowPaymentOption.CREDIT_CARD:
      return (
        <React.Fragment>
          <p>
            Visa and Mastercard charge 2.2% + $0.30 for each transaction. Amex
            charges a 3.5% flat fee. There’s an additional 1% fee for non-US
            cards.
          </p>
          {encourageBankDonation()}
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {getEveryNoPlatformFee()}
        </React.Fragment>
      );
    case DonationFlowPaymentOption.BANK:
      return (
        <React.Fragment>
          <p>
            Every.org currently covers the small ACH fees for donations made
            with a bank.
          </p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {getEveryNoPlatformFee()}
        </React.Fragment>
      );
    case DonationFlowPaymentOption.PAYPAL:
      return (
        <React.Fragment>
          <p>
            PayPal charges 1.99% + $0.49 for each transaction. There’s an
            additional 1.5% fee for non-US donors.
          </p>
          {encourageBankDonation()}
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {getEveryNoPlatformFee()}
        </React.Fragment>
      );
    case DonationFlowPaymentOption.VENMO:
      return (
        <React.Fragment>
          <p>
            Venmo charges 1.99% + $0.49 for each transaction. There’s an
            additional 1.5% fee for non-US donors.
          </p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {getEveryNoPlatformFee()}
        </React.Fragment>
      );
    case DonationFlowPaymentOption.CRYPTO:
      return (
        <React.Fragment>
          <p>
            Our exchanges charge a 1% flat fee to automatically liquidate
            cryptocurrency immediately after donation. Every.org does not charge
            any fees of our own. While usually not recommended, for donations
            worth over $100k, donors have the option to email crypto@every.org
            to arrange for manual exchange and potentially a lower fee. But even
            for large donations, we encourage you to immediately donate on the
            website. Donating here is the most straightforward and secure method
            to minimize risk of phishing or delays and loss caused by slippage.
          </p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {getEveryNoPlatformFee()}
        </React.Fragment>
      );
    case DonationFlowPaymentOption.STOCKS:
      return (
        <React.Fragment>
          <p>
            Every.org covers all fees for stock donations, so usually no! For
            some mutual funds there is a small brokerage fee which will be
            deducted.
          </p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {getEveryNoPlatformFee()}
        </React.Fragment>
      );
    case DonationFlowPaymentOption.DAF:
      return (
        <React.Fragment>
          <p>
            We do not charge any fees to accept DAF gifts, and currently cover
            all the Chariot fees.
          </p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {getEveryNoPlatformFee()}
        </React.Fragment>
      );
    case DonationFlowPaymentOption.IRA:
      return (
        <React.Fragment>
          <p>We do not charge any fees to accept IRA gifts.</p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {getEveryNoPlatformFee()}
        </React.Fragment>
      );
    default:
      return (
        <React.Fragment>
          <p>There may be third-party fees.</p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {getEveryNoPlatformFee()}
        </React.Fragment>
      );
  }
}

function getCoverCostsFeeDescription(
  paymentOption: DonationFlowPaymentOption | undefined,
  nonprofit: ContextNonprofit,
  parentNonprofit?: ContextNonprofit,
  isInternational?: boolean
) {
  const coverCostsBeginning = (
    <p>
      When you cover costs, you&#39;re covering payment processing fees
      {getNfgDisclaimer(nonprofit, parentNonprofit)
        ? ", disbursement fees,"
        : ""}{" "}
      and Every.org&#39;s operating costs.
    </p>
  );
  const coverCostsEnding = (
    <p>
      The remainder helps sustain Every.org, the nonprofit powering this
      fundraising platform.
    </p>
  );
  switch (paymentOption) {
    case DonationFlowPaymentOption.PAYMENT_REQUEST:
    case DonationFlowPaymentOption.CREDIT_CARD:
      return (
        <React.Fragment>
          {coverCostsBeginning}
          <p>
            Payment processing fees go directly to the processor, not Every.org.
            Visa and Mastercard charge 2.2% + $0.30 for each transaction. Amex
            charges a 3.5% flat fee.
            {isInternational
              ? " International cards incur an additional 1% fee."
              : ""}
          </p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {coverCostsEnding}
        </React.Fragment>
      );
    case DonationFlowPaymentOption.PAYPAL:
      return (
        <React.Fragment>
          {coverCostsBeginning}
          <p>
            PayPal charges 1.99% + $0.49 for each transaction. There’s an
            additional 1.5% fee for non-US donors.
          </p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {coverCostsEnding}
        </React.Fragment>
      );
    case DonationFlowPaymentOption.VENMO:
      return (
        <React.Fragment>
          {coverCostsBeginning}
          <p>
            Venmo charges 1.99% + $0.49 for each transaction. There’s an
            additional 1.5% fee for non-US donors.
          </p>
          {getNfgDisclaimer(nonprofit, parentNonprofit)}
          {coverCostsEnding}
        </React.Fragment>
      );
  }

  return getFeeDescription(paymentOption, nonprofit, parentNonprofit);
}

function encourageBankDonation() {
  return (
    <p>
      Don’t like fees? Neither do we! Donate via bank and then Every.org will
      cover the small ACH fee.
    </p>
  );
}

export const FaqComponent = ({
  nonprofit,
  fundraiser,
  paymentOption,
  coverCostsMode = false,
  isInternational,
  showStartFundraiserLink = true,
}: {
  nonprofit: ContextNonprofit;
  fundraiser?: FundraiserResponse;
  paymentOption: DonationFlowPaymentOption | undefined;
  coverCostsMode?: boolean;
  isInternational?: boolean;
  showStartFundraiserLink?: boolean;
}) => {
  const parentNonprofit = useParentNonprofit(nonprofit);
  const embedMode = useEmbedMode();

  const isEverydotorgRecipient = EVERYDOTORG_NONPROFIT_IDS.includes(
    parentNonprofit?.id || nonprofit.id
  );

  const faqDataList: AccordionItemTypes[] = [
    ...(nonprofit?.id === UNDP_NONPROFIT_ID
      ? [
          {
            id: "undp-large-donation",
            title: "Are there special requirements for larger donations?",
            description: (
              <p>
                For donations of{" "}
                {
                  displayCurrencyValue({
                    value: { currency: Currency.USD, amount: new Big(5000) },
                  }).formatted
                }{" "}
                or above, please contact us directly before making your
                contribution. This allows us to ensure that your generous gift
                is processed correctly and directed to the area where it can
                make the most impact. For further assistance, please reach out
                to us at{" "}
                <Link
                  data-tname="undpEmailLink"
                  to={mailToLink({
                    address: UNDP_EMAIL,
                  })}
                  appearance={LinkAppearance.HYPERLINK_UNCOLORED}
                >
                  {UNDP_EMAIL}
                </Link>
                .
              </p>
            ),
          },
        ]
      : []),
    ...(parentNonprofit?.id === MYRIAD_NONPROFIT_ID
      ? [
          {
            id: "myriad-check-wire",
            title: "How do I donate via Check or Wire?",
            description: (
              <React.Fragment>
                <p>
                  For gifts by check: Write your check to Myriad USA, write
                  &quot;
                  {nonprofit.name}&quot; in the memo section of the check, and
                  send it to Myriad USA, 551 Fifth Avenue, Suite 2400, New York,
                  NY 10176.
                </p>
                <p>
                  Gifts by wire transfer or to contribute other types of
                  property: Email Myriad USA at:{" "}
                  <Link
                    data-tname="dafDonationEmailLink"
                    to={mailToLink({
                      address: MYRIAD_EMAIL,
                    })}
                    appearance={LinkAppearance.HYPERLINK_UNCOLORED}
                  >
                    {MYRIAD_EMAIL}
                  </Link>{" "}
                  or phone{" "}
                  <Link
                    data-tname="dafDonationEmailLink"
                    to={`tel:${MYRIAD_PHONE}`}
                    appearance={LinkAppearance.HYPERLINK_UNCOLORED}
                  >
                    {MYRIAD_PHONE}
                  </Link>
                  .
                </p>
              </React.Fragment>
            ),
          },
        ]
      : []),
    ...(paymentOption === DonationFlowPaymentOption.IRA
      ? [
          {
            id: "ira-what-is",
            title: "What is an IRA QCD?",
            description: (
              <React.Fragment>
                <p>
                  A Qualified Charitable Distribution (QCD) lets individuals age
                  70½ or older donate directly from a traditional IRA, up to
                  $111,000 per year, without it counting as taxable income.
                </p>
                <p>
                  <Link
                    data-tname="iraQcdArticleLink"
                    appearance={LinkAppearance.HYPERLINK}
                    to={IRA_QCD_SUPPORT_URL}
                    target="_blank"
                  >
                    Learn more about donating from your IRA
                  </Link>
                </p>
              </React.Fragment>
            ),
          },
        ]
      : []),
    {
      id: "intro",
      title: "How does Every.org accept my donation?",
      description: (
        <React.Fragment>
          <p>
            Your donation is made to Every.org, a US 501(c)(3) Public Charity.
            Once a gift has completed processing, Every.org will immediately
            send you a receipt by email.{" "}
            {!isEverydotorgRecipient &&
              getDisbursementDescription(nonprofit, parentNonprofit, {
                linkToDisbursements: !embedMode,
              })}
          </p>
          {!isEverydotorgRecipient && (
            <p>
              This process ensures your eligibility for a tax deduction, enables
              you to consolidate your gift records, and reduces the burden on{" "}
              {getGranteeName(nonprofit, parentNonprofit)}.
            </p>
          )}
        </React.Fragment>
      ),
    },
    {
      id: "fees",
      title: coverCostsMode
        ? "How are costs calculated?"
        : "Are there any fees?",
      description: coverCostsMode
        ? getCoverCostsFeeDescription(
            paymentOption,
            nonprofit,
            parentNonprofit,
            isInternational
          )
        : getFeeDescription(paymentOption, nonprofit, parentNonprofit),
    },
    {
      id: "receipt",
      title: "Will I receive a tax-deductible receipt for my donation?",
      description:
        paymentOption === DonationFlowPaymentOption.DAF ? (
          <p>{DAF_DEDUCTIBLE_STATEMENT}</p>
        ) : paymentOption === DonationFlowPaymentOption.IRA ? (
          <p>{IRA_DEDUCTIBLE_STATEMENT}</p>
        ) : (
          <React.Fragment>
            <p>
              Yes. After your donation payment is confirmed, you will
              immediately get a tax-deductible receipt emailed to you.
            </p>
            <p>
              {getTaxDeductibleStatement(
                nonprofit,
                fundraiser,
                parentNonprofit
              )}
            </p>
            <p>
              Additionally, if you have an Every.org account, you can always get
              a single itemized receipt that shows all your donations in a given
              year.
            </p>
          </React.Fragment>
        ),
    },
    {
      id: "p2p",
      mobileOnly: true,
      omit: !showStartFundraiserLink,
      title: `How else can I support ${nonprofit.name}?`,
      description: (
        <React.Fragment>
          <p>
            You can also rally your friends, family, and social networks to
            support this nonprofit by starting your own fundraiser for them.{" "}
            <Link
              appearance={LinkAppearance.HYPERLINK_UNCOLORED}
              to={getRoutePath({
                format: URLFormat.RELATIVE,
                name: ClientRouteName.FUNDRAISER_CREATE,
                tokens: { nonprofitSlug: nonprofit.primarySlug },
              })}
              data-tname={"DonationFaq--seeTerms"}
            >
              Start a fundraiser for {nonprofit.name}
            </Link>
          </p>
        </React.Fragment>
      ),
    },
  ];

  return (
    // key forces re-render whole component when paymentOption changes
    // this fixes the "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node." error
    // this error occurs when the user uses the Google Translate extension on the page (or any extension that modifies the DOM)
    // https://github.com/facebook/react/issues/11538
    <DonateV3GridCard key={paymentOption}>
      <Accordion dataList={faqDataList} />
    </DonateV3GridCard>
  );
};
